import { useState, useCallback, useRef } from "react";
import { Tradesperson } from "@/lib/types";
import { getUserTradespeople, searchGlobalTradespeople } from "@/lib/supabase/trades";
import { toast } from "sonner";
import tradeTypeMap from "@/lib/compatibility/tradeTypeMap";
import keywordMap from "@/lib/compatibility/keywordMap";
import { debug } from "@/lib/utils";

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Finds trade types that match the given keyword
 * @param keyword - The keyword to match against
 * @returns Array of matching trade types
 */
function findTradeTypesForKeyword(keyword: string): string[] {
  const lowerKeyword = keyword.toLowerCase();
  const tradeTypes: string[] = [];
  
  // First check if the keyword matches directly to a category in keywordMap
  // This helps us bridge from user input to our internal categories
  let internalCategory = null;
  for (const [key, category] of Object.entries(keywordMap)) {
    if (lowerKeyword.includes(key.toLowerCase())) {
      internalCategory = category;
      break;
    }
  }
  
  debug.log(`Keyword "${keyword}" mapped to internal category: ${internalCategory}`);
  
  // Search all trade types for matching keywords
  for (const [tradeType, keywords] of Object.entries(tradeTypeMap.trade_types)) {
    // Direct keyword match in trade type keywords
    if (keywords.some(k => lowerKeyword.includes(k.toLowerCase()))) {
      tradeTypes.push(tradeType);
      continue;
    }
    
    // If we found an internal category, look for any trade type that has keywords
    // matching that category's keywords (indirect matching)
    if (internalCategory) {
      for (const [key, category] of Object.entries(keywordMap)) {
        if (category === internalCategory && 
            keywords.some(k => k.toLowerCase().includes(key.toLowerCase()))) {
          tradeTypes.push(tradeType);
          break;
        }
      }
    }
  }
  
  // Make sure we have unique trade types
  return [...new Set(tradeTypes)];
}
/**
 * Custom hook for searching tradespeople
 * 
 * @param userId - The user ID to search trades for
 * @returns An object containing tradespeople, loading state, and search function
 */
export function useTradesSearch(userId: string) {
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const errorShownRef = useRef(false);
  
  // Wrap in useCallback to maintain function reference between renders
  const searchTradespeople = useCallback(async (keyword: string) => {
    // Validate userId format
    if (!userId || !UUID_REGEX.test(userId)) {
      debug.error('Invalid user ID format:', userId);
      // Only show toast once
      if (!errorShownRef.current) {
        toast.error('Unable to search tradespeople (invalid user ID)');
        errorShownRef.current = true;
      }
      return;
    }
    
    // Set loading state before any async operations
    setIsSearching(true);
    
    try {
      // Find trade types that match the keyword
      const matchingTradeTypes = findTradeTypesForKeyword(keyword);
      debug.log(`Found matching trade types for "${keyword}":`, matchingTradeTypes);
      
      // If no matching trade types, don't return empty - instead, we'll
      // do a more general search to show a range of options
      let isFallbackSearch = false;
      if (matchingTradeTypes.length === 0) {
        debug.log(`No matching trade types found for "${keyword}" - using fallback search`);
        isFallbackSearch = true;
        // Don't exit early - continue to show user's saved tradespeople and/or general options
      }
      
      // Get user's saved tradespeople
      const userTradespeople = await getUserTradespeople(userId);
      
      // Filter user tradespeople by trade type if we have matches
      let filteredUserTradespeople = userTradespeople;
      if (!isFallbackSearch && matchingTradeTypes.length > 0) {
        filteredUserTradespeople = userTradespeople.filter(ut => {
          const tradesperson = ut.tradesperson;
          if (!tradesperson || !tradesperson.trade_type) return false;
          
          // Check if this tradesperson's trade type matches any in our list
          return matchingTradeTypes.includes(tradesperson.trade_type);
        });
      }
      debug.log(`Found ${filteredUserTradespeople.length} matching user tradespeople`);
      
      // Search global tradespeople
      let globalTradespeople;
      if (isFallbackSearch) {
        // For fallback, do a more general search without type filtering
        globalTradespeople = await searchGlobalTradespeople(keyword);
      } else {
        // Regular search with trade type filtering
        globalTradespeople = await searchGlobalTradespeople(keyword, matchingTradeTypes);
      }
      
      debug.log(`Found ${globalTradespeople.length} matching global tradespeople`);
      
      // Reset error flag on success
      errorShownRef.current = false;
      
      // Combine and deduplicate results
      const combinedTradespeople = [
        ...filteredUserTradespeople.map(ut => ut.tradesperson)
          .filter((tp): tp is Tradesperson => tp !== undefined && tp !== null),
        ...globalTradespeople
      ]
        // Remove duplicates based on ID
        .filter((tp, index, self) => 
          index === self.findIndex(t => t.id === tp.id)
        );
      
      debug.log(`Total: ${combinedTradespeople.length} combined tradespersons after deduplication`);
      
      setTradespeople(combinedTradespeople);
    } catch (error) {
      debug.error('Error searching tradespeople:', error);
      // Only show toast once until successful search
      if (!errorShownRef.current) {
        toast.error('Failed to search tradespeople');
        errorShownRef.current = true;
      }
    } finally {
      // Always reset loading state when done
      setIsSearching(false);
    }
  }, [userId]); // Only depends on userId, which shouldn't change often

  return {
    tradespeople,
    isSearching,
    searchTradespeople
  };
}